/**
 * API Client
 * Centralized HTTP client for backend API calls
 */

import axios, { AxiosInstance, AxiosError } from 'axios';
import { auth } from './firebase';
import { getApiUrl } from '../utils/apiUrl';

// Get the API URL dynamically based on Firebase project
const API_BASE_URL = getApiUrl();

// Create axios instance
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error getting auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle errors
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response) {
      // Server responded with error
      console.error('API Error:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });

      // Handle specific status codes
      if (error.response.status === 401) {
        // Unauthorized - redirect to login
        window.location.href = '/auth/sign-in';
      } else if (error.response.status === 403) {
        // Forbidden - insufficient permissions
        console.error('Access denied - insufficient permissions');
      }
    } else if (error.request) {
      // Request made but no response
      console.error('Network Error:', error.message);
    } else {
      // Something else happened
      console.error('Request Error:', error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
